import { useState } from "react"
import { useParams } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  FileSignature,
  FileCheck,
  FileX,
  CalendarClock,
  Building2,
  Shield,
  Download,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import * as O from "fp-ts/Option"
import { pipe } from "fp-ts/function"
import { serverDetail } from "@/services/public-fetch"
import { useToast } from "@/hooks/use-toast"
import {
  getPublicJobDescription,
  signPublicJobDescription,
  publicJobDescriptionPdfUrl,
  type PublicJobDescriptionResponse,
  type PublicJobDescriptionStatus,
} from "@/services/public-job-description"
import { TenantClosedNotice } from "@/components/TenantClosedNotice"
import { isTenantClosedError } from "@/lib/tenant-closed"
import { formatIsoDateFr } from "@/lib/organisation-day"

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return ""
  return new Date(iso).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ""
  return formatIsoDateFr(iso, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  })
}

export default function PublicJobDescriptionPage() {
  const { token } = useParams<{ token: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [consentChecked, setConsentChecked] = useState(false)

  const query = useQuery({
    queryKey: ["public-job-description", token],
    queryFn: () => getPublicJobDescription(token!),
    enabled: Boolean(token),
    retry: false,
  })

  const signMutation = useMutation({
    mutationFn: () => signPublicJobDescription(token!),
    onSuccess: (response) => {
      toast({
        title: "Document signé",
        description: "Merci pour votre signature.",
      })
      // Backend invalide le token après sign (`SET staff_public_token = NULL`
      // en mode A, idem `manager_public_token` en mode B final). Refetch =
      // 404 → page bascule en `jd-revoked-state` au lieu de la confirmation.
      // On patche le cache localement avec le nouveau status + timestamp pour
      // déclencher le rendu de `SignedConfirmation` (cf. test E2E :69).
      const signedAt = new Date().toISOString()
      const isFinal = response.is_final
      queryClient.setQueryData<PublicJobDescriptionResponse>(
        ["public-job-description", token],
        (prev) =>
          prev
            ? {
                ...prev,
                status: isFinal
                  ? "signed"
                  : response.new_status === "pending_manager"
                  ? "pending_manager"
                  : prev.status,
                signed_at: isFinal ? signedAt : prev.signed_at,
                staff_signed_at:
                  response.signer_role === "staff"
                    ? signedAt
                    : prev.staff_signed_at,
                manager_signed_at:
                  response.signer_role === "manager"
                    ? signedAt
                    : prev.manager_signed_at,
              }
            : prev,
      )
    },
    onError: (err: Error) => {
      toast({
        title: "Signature impossible",
        description: err.message,
        variant: "destructive",
      })
    },
  })

  if (!token) {
    return (
      <PublicShell>
        <ErrorState
          title="Lien invalide"
          message="Aucun jeton de signature n'a été fourni."
        />
      </PublicShell>
    )
  }

  if (query.isLoading) {
    return (
      <PublicShell>
        <div
          className="py-16 text-center text-muted-foreground"
          data-test="jd-loading"
        >
          Chargement de la fiche de poste...
        </div>
      </PublicShell>
    )
  }

  if (isTenantClosedError(query.error)) {
    return (
      <PublicShell>
        <TenantClosedNotice />
      </PublicShell>
    )
  }

  if (query.isError || !query.data) {
    const errAny = query.error as (Error & { status?: number }) | null
    const status = errAny?.status
    // Raison donnée par le serveur, sinon le texte de repli.
    const serverMessage = (fallback: string) => pipe(serverDetail(query.error), O.getOrElse(() => fallback))
    // 404 (token inconnu — pas de row en DB) et 410 (révoqué — action explicite)
    // sont indistinguables côté utilisateur : "ce lien ne marche pas, demande
    // un nouveau". On unifie sur `jd-revoked-state` pour ne pas multiplier les
    // états error qui n'aident pas l'utilisateur final.
    if (status === 404 || status === 410) {
      return (
        <PublicShell>
          <ErrorState
            title={status === 410 ? "Lien révoqué" : "Lien introuvable"}
            message={serverMessage(
              status === 410
                ? "Ce lien de signature a été révoqué par l'organisme. Demandez une nouvelle invitation."
                : "Ce lien de signature est inconnu ou n'existe plus. Demandez une nouvelle invitation à votre organisme.",
            )}
            dataTest="jd-revoked-state"
          />
        </PublicShell>
      )
    }
    if (status === 422) {
      return (
        <PublicShell>
          <ErrorState
            title="Lien expiré"
            message="Ce lien a expiré (validité 7 jours). Contactez votre organisme de formation pour en obtenir un nouveau."
            dataTest="jd-expired-state"
          />
        </PublicShell>
      )
    }
    return (
      <PublicShell>
        <ErrorState
          title="Fiche introuvable"
          message={serverMessage("Impossible de charger la fiche de poste.")}
          dataTest="jd-error-state"
        />
      </PublicShell>
    )
  }

  const jd = query.data
  const alreadySigned = jd.status === "signed"
  const canSign = jd.status === "pending_staff" || jd.status === "pending_manager"

  return (
    <PublicShell tenantName={jd.tenant.name} tenantLogo={jd.tenant.logo_url}>
      <div className="space-y-6" data-test="public-job-description-page">
        <StatusHeader jd={jd} />

        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle data-test="jd-staff-name">
                Fiche de poste de {jd.staff.full_name}
              </CardTitle>
              {jd.staff.job_title ? (
                <p
                  className="mt-1 text-sm text-muted-foreground"
                  data-test="jd-staff-job-title"
                >
                  {jd.staff.job_title}
                </p>
              ) : null}
            </div>
            {jd.pdf_url ? (
              <Button asChild variant="outline" size="sm" data-test="jd-download-pdf">
                <a
                  href={publicJobDescriptionPdfUrl(token)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Télécharger le PDF
                </a>
              </Button>
            ) : null}
          </CardHeader>
          <CardContent className="space-y-6">
            <StaffIdentitySection jd={jd} />
            <MissionsSection jd={jd} />
            <SkillsSection jd={jd} />
            <QualiopiFunctionsSection jd={jd} />
            {jd.manager?.full_name ? (
              <section data-test="jd-manager-section">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  Manager
                </h2>
                <p className="mt-2 text-sm">{jd.manager.full_name}</p>
              </section>
            ) : null}
          </CardContent>
        </Card>

        {alreadySigned ? (
          <SignedConfirmation jd={jd} />
        ) : canSign ? (
          <SignaturePanel
            jd={jd}
            consentChecked={consentChecked}
            onConsentChange={setConsentChecked}
            onSign={() => signMutation.mutate()}
            isSigning={signMutation.isPending}
          />
        ) : null}

        <GdprNotice notice={jd.rgpd_notice} />
      </div>
    </PublicShell>
  )
}

function PublicShell({
  children,
  tenantName,
  tenantLogo,
}: {
  children: React.ReactNode
  tenantName?: string
  tenantLogo?: string | null
}) {
  return (
    <div className="min-h-screen bg-slate-50" data-test="public-shell">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-4 sm:px-6">
          {tenantLogo ? (
            <img
              src={tenantLogo}
              alt={tenantName ?? "Logo"}
              className="h-10 w-10 rounded object-contain"
              data-test="tenant-logo"
            />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded bg-slate-100 text-slate-500">
              <Building2 className="h-5 w-5" />
            </span>
          )}
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Signature électronique
            </p>
            <p
              className="truncate text-sm font-medium text-slate-900"
              data-test="public-tenant-name"
            >
              {tenantName ?? "Organisme de formation"}
            </p>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">{children}</main>
      <footer className="mx-auto max-w-3xl px-4 py-6 text-center text-xs text-muted-foreground sm:px-6">
        Document généré par Qualneo
      </footer>
    </div>
  )
}

function StatusHeader({ jd }: { jd: PublicJobDescriptionResponse }) {
  const meta = statusMeta(jd.status)
  const Icon = meta.icon
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border p-4 ${meta.containerClass}`}
      data-test="jd-status-banner"
      data-status={jd.status}
    >
      <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
      <div className="flex-1 space-y-1">
        <p className="text-sm font-semibold" data-test="jd-status-label">
          {meta.title}
        </p>
        <p className="text-sm text-muted-foreground" data-test="jd-status-message">
          {meta.subtitle(jd)}
        </p>
        <p className="text-xs text-muted-foreground" data-test="jd-version">
          Version {jd.version_number}
        </p>
      </div>
    </div>
  )
}

function statusMeta(status: PublicJobDescriptionStatus): {
  icon: React.ComponentType<{ className?: string }>
  containerClass: string
  title: string
  subtitle: (jd: PublicJobDescriptionResponse) => string
} {
  switch (status) {
    case "pending_staff":
      return {
        icon: FileSignature,
        containerClass: "border-amber-200 bg-amber-50 text-amber-900",
        title: "Signature attendue",
        subtitle: () =>
          "Votre signature est requise pour valider votre fiche de poste.",
      }
    case "pending_manager":
      return {
        icon: FileSignature,
        containerClass: "border-amber-200 bg-amber-50 text-amber-900",
        title: "Signature manager attendue",
        subtitle: () =>
          "La fiche a été signée par le collaborateur. Le manager doit maintenant signer.",
      }
    case "signed":
      return {
        icon: FileCheck,
        containerClass: "border-emerald-200 bg-emerald-50 text-emerald-900",
        title: "Document signé",
        subtitle: (jd) =>
          `Signature finale enregistrée le ${formatDateTime(jd.signed_at)}.`,
      }
    case "revoked":
      return {
        icon: FileX,
        containerClass: "border-rose-200 bg-rose-50 text-rose-900",
        title: "Lien révoqué",
        subtitle: (jd) =>
          `Ce lien a été révoqué le ${formatDateTime(jd.revoked_at)}.`,
      }
    case "expired":
      return {
        icon: CalendarClock,
        containerClass: "border-slate-200 bg-slate-50 text-slate-900",
        title: "Lien expiré",
        subtitle: () =>
          "La période de validité du lien est dépassée. Contactez votre organisme.",
      }
  }
}

function StaffIdentitySection({ jd }: { jd: PublicJobDescriptionResponse }) {
  return (
    <section data-test="jd-identity-section">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Collaborateur
      </h2>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-xs text-muted-foreground">Nom</dt>
          <dd className="font-medium" data-test="jd-identity-name">
            {jd.staff.full_name}
          </dd>
        </div>
        {jd.staff.employee_number ? (
          <div>
            <dt className="text-xs text-muted-foreground">Matricule</dt>
            <dd data-test="jd-identity-employee-number">
              {jd.staff.employee_number}
            </dd>
          </div>
        ) : null}
        {jd.staff.department ? (
          <div>
            <dt className="text-xs text-muted-foreground">Département</dt>
            <dd data-test="jd-identity-department">{jd.staff.department}</dd>
          </div>
        ) : null}
        {jd.staff.hire_date ? (
          <div>
            <dt className="text-xs text-muted-foreground">Date d'embauche</dt>
            <dd data-test="jd-identity-hire-date">
              {formatDate(jd.staff.hire_date)}
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  )
}

function MissionsSection({ jd }: { jd: PublicJobDescriptionResponse }) {
  return (
    <section data-test="jd-missions-section">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Missions
      </h2>
      {jd.missions.length === 0 ? (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-test="jd-missions-empty"
        >
          Aucune mission renseignée.
        </p>
      ) : (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
          {jd.missions.map((m) => (
            <li key={m.id} data-test="jd-mission-item">
              {m.label}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function SkillsSection({ jd }: { jd: PublicJobDescriptionResponse }) {
  return (
    <section data-test="jd-skills-section">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Compétences
      </h2>
      {jd.skills.length === 0 ? (
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-test="jd-skills-empty"
        >
          Aucune compétence renseignée.
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {jd.skills.map((s) => (
            <Badge
              key={s.id}
              variant="secondary"
              data-test="jd-skill-badge"
            >
              {s.label}
            </Badge>
          ))}
        </div>
      )}
    </section>
  )
}

function QualiopiFunctionsSection({ jd }: { jd: PublicJobDescriptionResponse }) {
  if (jd.qualiopi_functions.length === 0) return null
  return (
    <section data-test="jd-qualiopi-section">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Fonctions Qualiopi
      </h2>
      <div className="mt-3 flex flex-wrap gap-2">
        {jd.qualiopi_functions.map((fn) => (
          <Badge
            key={fn.id}
            className="bg-rose-100 text-rose-800"
            data-test={`jd-qualiopi-fn-${fn.code}`}
          >
            {fn.label}
          </Badge>
        ))}
      </div>
    </section>
  )
}

function SignaturePanel({
  jd,
  consentChecked,
  onConsentChange,
  onSign,
  isSigning,
}: {
  jd: PublicJobDescriptionResponse
  consentChecked: boolean
  onConsentChange: (checked: boolean) => void
  onSign: () => void
  isSigning: boolean
}) {
  const signerLabel =
    jd.signer_role === "manager" ? "le manager" : "le collaborateur"
  return (
    <Card data-test="jd-signature-panel">
      <CardHeader>
        <CardTitle className="text-base">Signer ce document</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p>
          En cliquant sur le bouton ci-dessous, vous certifiez être {signerLabel}
          {" "}désigné et accepter le contenu de cette fiche de poste.
        </p>
        <label
          className="flex items-start gap-2 text-xs text-muted-foreground"
          data-test="jd-consent-label"
        >
          <input
            type="checkbox"
            checked={consentChecked}
            onChange={(e) => onConsentChange(e.currentTarget.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300"
            data-test="jd-consent-checkbox"
          />
          <span>
            J'ai lu la fiche de poste, j'accepte son contenu et je consens à
            l'enregistrement de mon adresse IP, de l'horodatage et de mon
            identité pour preuve d'authenticité (RGPD art. 6.1.b et 6.1.c).
          </span>
        </label>
        <Button
          onClick={onSign}
          disabled={!consentChecked || isSigning}
          className="w-full bg-rose-500 hover:bg-rose-600"
          data-test="jd-sign-button"
        >
          {isSigning ? "Signature en cours..." : "Accepter et signer"}
        </Button>
      </CardContent>
    </Card>
  )
}

function SignedConfirmation({ jd }: { jd: PublicJobDescriptionResponse }) {
  return (
    <Card
      className="border-emerald-200 bg-emerald-50"
      data-test="jd-signed-confirmation"
    >
      <CardContent className="space-y-2 pt-6 text-sm text-emerald-900">
        <div className="flex items-center gap-2 font-medium">
          <FileCheck className="h-4 w-4" />
          Document déjà signé le {formatDateTime(jd.signed_at)}
        </div>
        {jd.staff_signed_at ? (
          <p className="text-xs">
            Signé par le collaborateur le {formatDateTime(jd.staff_signed_at)}.
          </p>
        ) : null}
        {jd.manager_signed_at ? (
          <p className="text-xs">
            Signé par le manager le {formatDateTime(jd.manager_signed_at)}.
          </p>
        ) : null}
      </CardContent>
    </Card>
  )
}

/**
 * Information RGPD (art. 13) du signataire, rédigée par l'API.
 * La page affiche le texte servi, sans le réécrire.
 * @example
 * <GdprNotice notice={jd.rgpd_notice} />
 */
export function GdprNotice({ notice }: { notice: string }) {
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 text-xs leading-relaxed text-muted-foreground"
      data-test="jd-gdpr-notice"
    >
      <div className="mb-2 flex items-center gap-2 font-medium text-slate-700">
        <Shield className="h-4 w-4" />
        Information RGPD
      </div>
      <p className="whitespace-pre-line">{notice}</p>
    </div>
  )
}

function ErrorState({
  title,
  message,
  dataTest = "jd-error-state",
}: {
  title: string
  message: string
  dataTest?: string
}) {
  return (
    <Card data-test={dataTest}>
      <CardContent className="space-y-2 py-12 text-center">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{message}</p>
      </CardContent>
    </Card>
  )
}
